import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestion, faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';
import '../css/faqsection.css';

const FaqSection = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [activeTab, setActiveTab] = useState('General');

    const allFaqData = {
        General: [
            {
                question: "What is general insurance and how is it different from life insurance?",
                answer: "General insurance provides coverage for non-life assets like vehicles, health, travel, home, and gadgets. Life insurance, on the other hand, offers financial protection in case of death or after a set period."
            },
            {
                question: " Is it mandatory to have motor insurance in India?",
                answer: "Yes. As per Indian law, it’s mandatory to have at least third-party motor insurance for all vehicles. Driving without it is a punishable offense."
            },
            {
                question: " What does a travel insurance policy usually cover?",
                answer: "Travel insurance typically covers medical emergencies, trip cancellations, lost baggage, and passport loss while traveling domestically or internationally."
            },
            {
                question: " Can I claim home insurance if damage is caused by natural disasters?",
                answer: "Most home insurance policies cover damage caused by natural disasters like floods, earthquakes, and storms, provided these perils are specifically included in your policy terms and conditions."
            },
            {
                question: "What is the claim process for general insurance policies?",
                answer: "Notify the insurer immediately, submit necessary documents, get the damage surveyed (if required), and receive claim approval. Many insurers now offer easy app-based or online claims too."
            }
        ],
        Car: [
            {
                question: " What types of car insurance are available in India?",
                answer: "Third-party insurance (mandatory by law) and Comprehensive insurance (covers your car + third-party). You can also add add-ons like zero depreciation, engine cover, etc."
            },
            {
                question: "What is a No Claim Bonus (NCB)?",
                answer: "NCB is a reward you get for not making any claims during the policy year. It gives you a discount (up to 50%) on renewal premium."
            },
            {
                question: "Can I transfer my car insurance to a new owner?",
                answer: "Yes, you can transfer the policy when selling the car. The buyer must apply for a name transfer within 14 days of purchase."
            },
            {
                question: " Is it necessary to have insurance for an old or second-hand car?",
                answer: "Yes. All cars, new or old, must have at least a valid third-party insurance policy to drive legally in India."
            },
            {
                question: "What is Zero Depreciation Cover in car insurance?",
                answer: "It’s an add-on that ensures no deduction for depreciation during a claim. You get full repair/replacement cost without value reduction for parts."
            }
        ],
        Bike: [
            {
                question: " Is bike insurance mandatory in India?",
                answer: "Yes. As per the Motor Vehicles Act, every two-wheeler must have at least a third-party insurance policy to legally ride on Indian roads."
            },
            {
                question: " What is the difference between third-party and comprehensive bike insurance?",
                answer: "Third-party insurance covers damage to others (people/property). Comprehensive insurance covers third-party + your bike’s own damage (accidents, fire, theft, natural disasters)."
            },
            {
                question: " How can I reduce my bike insurance premium?",
                answer: "Not making small claims (to earn NCB), Installing anti-theft devices, Choosing voluntary deductibles, and Renewing your policy on time."
            },
            {
                question: " Can I renew my bike insurance after it expires?",
                answer: "Yes, but it’s best to renew before expiry. If you miss it, you may face inspection and lose No Claim Bonus."
            },
            {
                question: "What is an IDV in bike insurance?",
                answer: "IDV stands for Insured Declared Value—it’s the current market value of your bike. It’s the maximum amount you can claim."
            }
        ],
        Health: [
            {
                question: " What is health insurance and why is it important?",
                answer: "Health insurance covers medical expenses like hospitalization, surgeries, and treatments. It protects your savings and ensures access to quality healthcare."
            },
            {
                question: "What is the difference between individual and family floater plans?",
                answer: "Individual plan covers only one person. Family floater covers your entire family under a single sum insured."
            },
            {
                question: "What is a waiting period in health insurance?",
                answer: "A waiting period is the time you must wait before certain diseases or pre-existing conditions are covered."
            },
            {
                question: "Can I get tax benefits from health insurance?",
                answer: "Yes. Premiums paid qualify for tax deduction under Section 80D—up to ₹25,000 (or ₹50,000 for senior citizens)."
            },
            {
                question: " What is cashless hospitalization?",
                answer: "Cashless hospitalization means the insurance company settles the bills directly with the network hospital."
            }
        ],
        Life: [
            {
                question: "What is life insurance and how does it work?",
                answer: "Life insurance provides financial support to your family. You pay premiums, and the insurer pays a lump sum (sum assured) to your nominee."
            },
            {
                question: "What is the difference between term insurance and endowment plans?",
                answer: "Term insurance offers pure life cover (no maturity benefit). Endowment plans offer both life cover and savings."
            },
            {
                question: " How much life insurance cover should I take?",
                answer: "Ideally, your life cover should be 10 to 15 times your annual income."
            },
            {
                question: "Can I have more than one life insurance policy?",
                answer: "Yes, you can have multiple life insurance policies from different insurers."
            },
            {
                question: "What is the free-look period in life insurance?",
                answer: "The free-look period is a 15-day window to cancel the policy if you're not satisfied."
            }
        ],
        Term: [
            {
                question: "What is term insurance and how is it different from other life insurance plans?",
                answer: "Term insurance is a pure life cover plan. It pays a fixed sum if you pass away during the term. It’s the most affordable way to secure your loved ones."
            },
            {
                question: "What is the ideal age to buy term insurance?",
                answer: "The earlier, the better. Ideally in your 20s or early 30s when premiums are low."
            },
            {
                question: "How much term insurance cover do I need?",
                answer: "A good thumb rule is 10 to 15 times your annual income."
            },
            {
                question: " Can I increase my term insurance coverage later?",
                answer: "Yes, some plans offer life-stage benefits, letting you increase cover after marriage or childbirth."
            },
            {
                question: " What happens if I survive the policy term?",
                answer: "Standard plans don’t offer payouts. However, Return of Premium (ROP) plans refund premiums paid at a higher cost."
            }
        ],
        Investment: [
            {
                question: " What is the best age to start investing?",
                answer: "The earlier you start, the better. Starting in your 20s allows compounding to grow your money significantly."
            },
            {
                question: "What are the safest investment options in India?",
                answer: "Options like PPF, NSC, fixed deposits, and government bonds are considered safe."
            },
            {
                question: "How much of my income should I invest?",
                answer: "A common rule is 50-30-20, where 20% of your income should go into investments."
            },
            {
                question: "What is SIP and how does it work?",
                answer: "SIP (Systematic Investment Plan) lets you invest a fixed amount regularly in mutual funds."
            },
            {
                question: "How is investing different from saving?",
                answer: "Saving is for short-term needs; investing is about growing your money for long-term goals like retirement."
            }
        ],
        Business: [
            {
                question: "What is business insurance and why is it important?",
                answer: "Business insurance protects companies from losses due to property damage, liabilities, or business interruptions."
            },
            {
                question: " What types of business insurance are available in India?",
                answer: "Includes Fire & Property, Liability, Marine, Group Health, and Cyber Liability Insurance."
            },
            {
                question: "Is business insurance mandatory in India?",
                answer: "Some types, like Third-Party Liability for commercial vehicles or Workmen’s Compensation, are mandatory."
            },
            {
                question: "Can small businesses and startups buy insurance too?",
                answer: "Yes, many insurers offer customizable SME-specific plans at affordable premiums."
            },
            {
                question: "Does business insurance cover cyber threats or data loss?",
                answer: "Yes, Cyber Liability Insurance covers financial losses due to data breaches and hacking."
            }
        ]
    };

    const currentFaqData = allFaqData[activeTab];
    const handleToggle = (index) => {
        setActiveIndex(prevIndex => (prevIndex === index ? null : index));
    };

    const handleTabClick = (tabName) => {
        setActiveTab(tabName);
        setActiveIndex(null);
    };

    return (
        <section className="faq-section-container">
            <Helmet>
                <title>Insurance Policy FAQs India | Understand Your Insurance Policy</title>
                <meta 
                    name="description" 
                    content="Get clarity with our insurance policy FAQs India. Find expert insurance policy explanation, tips to avoid mistakes, and guidance on how to understand your insurance policy." 
                />
                <meta 
                    name="keywords" 
                    content="insurance policy FAQs India, insurance policy explanation, how to understand your insurance policy, insurance policy mistakes to avoid, insurance guidance, policy consultation, insurance advice India" 
                />
                <link rel="canonical" href="https://policysamjho.com/faqs" />
                <script type="application/ld+json">
                    {JSON.stringify({
                      "@context": "https://schema.org",
                      "@type": "FAQPage",
                      "mainEntity": [
                        {
                          "@type": "Question",
                          "name": "How to understand your insurance policy with Policy Samjho?",
                          "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Policy Samjho provides clear insurance policy explanations and expert reviews to help you understand coverage, exclusions, and avoid common insurance policy mistakes."
                          }
                        },
                        {
                          "@type": "Question",
                          "name": "Can I get a personalized insurance policy review?",
                          "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Yes, you can upload your policy for a professional review and receive personalized insurance recommendations from our experts."
                          }
                        }
                      ]
                    })}
                </script>
            </Helmet>

            <div className="faq-content-wrapper">
                <div className="faq-header-wrapper">
                    <p className="faq-small-heading">Clarity in Insurance: Your Questions Answered</p>
                    <h1 className="faq-main-heading">Insurance Policy <span className="highlight-yellow">FAQs India</span></h1>
                </div>

                <nav className="faq-tabs-navigation">
                    {Object.keys(allFaqData).map((tabName) => (
                        <button
                            key={tabName}
                            className={`tab-button ${activeTab === tabName ? 'active' : ''}`}
                            onClick={() => handleTabClick(tabName)}
                        >
                            {tabName}
                        </button>
                    ))}
                </nav>

                <div className="faq-items-container">
                    {currentFaqData.map((item, index) => (
                        <div key={index} className={`faq-individual-item ${activeIndex === index ? 'active' : ''}`}>
                            <button className="faq-question-button" onClick={() => handleToggle(index)}>
                                <span className="faq-icon-wrapper"><FontAwesomeIcon icon={faQuestion} /></span>
                                {item.question}
                                <span className="faq-toggle-icon">
                                    <FontAwesomeIcon icon={activeIndex === index ? faAngleUp : faAngleDown} />
                                </span>
                            </button>
                            <div className="faq-answer-content" style={{ maxHeight: activeIndex === index ? 'max-content' : '0' }}>
                                <p>{item.answer}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default FaqSection;